import React, { useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { ShieldCheck, Check, X, Save, LockKeyhole } from "lucide-react";
import axios from "axios";
import authHeader from "../../../Auth/authHeader";
import { ApiLink } from "../../../APILink/ApiLink";
import { PasswordInput } from "./PasswordInput";
import "./UserPasswordTab.scss";

export default function UserPasswordTab() {
  const [formData, setFormData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [isSaving, setIsSaving] = useState(false);

  const passwordRules = useMemo(
    () => ({
      minLength: formData.newPassword.length >= 8,
      hasUpper: /[A-Z]/.test(formData.newPassword),
      hasNumber: /\d/.test(formData.newPassword),
      hasSymbol: /[^A-Za-z0-9]/.test(formData.newPassword),
    }),
    [formData.newPassword]
  );

  const allRulesPassed = Object.values(passwordRules).every(Boolean);

  const strengthScore = Object.values(passwordRules).filter(Boolean).length;
  const strengthLabel = ["Shume dobët", "Dobët", "Mesatar", "Mire", "Shume mire"][strengthScore];
  const strengthClass = ["very-weak", "weak", "medium", "strong", "very-strong"][strengthScore];

  const passwordsMatch =
    formData.confirmPassword.length > 0 &&
    formData.newPassword === formData.confirmPassword;

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.currentPassword || !formData.newPassword || !formData.confirmPassword) {
      toast.error("Ploteso te gjitha fushat e fjalekalimit.");
      return;
    }

    if (!allRulesPassed) {
      toast.error("Fjalekalimi i ri nuk i ploteson kriteret e sigurise.");
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      toast.error("Konfirmimi nuk perputhet me fjalekalimin e ri.");
      return;
    }

    try {
      setIsSaving(true);
      const headers = authHeader();

      await axios.post(
        ApiLink.API_URL + "AuthProva/changePassword",
        {
          currentPassword: formData.currentPassword,
          newPassword: formData.newPassword,
        },
        { headers }
      );

      setFormData({ currentPassword: "", newPassword: "", confirmPassword: "" });
      toast.success("Fjalekalimi u ndryshua me sukses!", {
        duration: 3500,
        style: {
          background: "#f0fdf4",
          color: "#15803d",
          border: "1px solid #bbf7d0",
          fontWeight: "600",
        },
        iconTheme: { primary: "#16a34a", secondary: "#f0fdf4" },
      });
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        "Ndryshimi i fjalekalimit deshtoi. Provo perseri.";
      toast.error(msg, {
        duration: 4000,
        style: {
          background: "#fef2f2",
          color: "#991b1b",
          border: "1px solid #fecaca",
          fontWeight: "600",
        },
        iconTheme: { primary: "#dc2626", secondary: "#fef2f2" },
      });
    } finally {
      setIsSaving(false);
    }
  };

  const rules = [
    { key: "minLength", label: "Minimum 8 karaktere" },
    { key: "hasUpper",  label: "Te pakten 1 shkronje e madhe" },
    { key: "hasNumber", label: "Te pakten 1 numer" },
    { key: "hasSymbol", label: "Te pakten 1 simbol (!, @, # ...)" },
  ];

  return (
    <div className="user-password-tab">
      {/* ── Header ─────────────────────────────────── */}
      <div className="user-password-tab__header">
        <div className="user-password-tab__header-icon-wrap">
          <LockKeyhole className="user-password-tab__header-icon" />
        </div>
        <div>
          <h2 className="user-password-tab__title">Siguria e Llogarise</h2>
          <p className="user-password-tab__subtitle">
            Ndrysho fjalekalimin e llogarise suaj. Sigurohuni qe te perdorni nje fjalekalim te forte.
          </p>
        </div>
      </div>

      {/* ── Two-column layout ──────────────────────── */}
      <div className="user-password-tab__body">

        {/* LEFT — form */}
        <div className="user-password-tab__form-col">
          <div className="user-password-tab__card">
            <p className="user-password-tab__card-title">Ndrysho fjalekalimin</p>

            <div className="user-password-tab__fields">
              <PasswordInput
                label="Fjalekalimi aktual"
                name="currentPassword"
                value={formData.currentPassword}
                onChange={handleChange}
                autoComplete="current-password"
                disabled={isSaving}
              />

              <div className="user-password-tab__divider" />

              <PasswordInput
                label="Fjalekalimi i ri"
                name="newPassword"
                value={formData.newPassword}
                onChange={handleChange}
                autoComplete="new-password"
                disabled={isSaving}
              />

              {/* Strength bar */}
              {formData.newPassword.length > 0 && (
                <div className="user-password-tab__strength">
                  <div className="user-password-tab__strength-bars">
                    {[0,1,2,3].map((i) => (
                      <span
                        key={i}
                        className={`user-password-tab__strength-bar ${
                          i < strengthScore ? `is-${strengthClass}` : ""
                        }`}
                      />
                    ))}
                  </div>
                  <span className={`user-password-tab__strength-label is-${strengthClass}`}>
                    {strengthLabel}
                  </span>
                </div>
              )}

              <PasswordInput
                label="Konfirmo fjalekalimin e ri"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                autoComplete="new-password"
                disabled={isSaving}
                error={formData.confirmPassword.length > 0 && !passwordsMatch}
                helperText={
                  formData.confirmPassword.length > 0 && !passwordsMatch
                    ? "Fjalekalimi nuk perputhet"
                    : ""
                }
              />
            </div>

            <div className="user-password-tab__actions">
              <button
                type="button"
                className="user-password-tab__save-btn"
                onClick={handleSubmit}
                disabled={isSaving}
              >
                {isSaving
                  ? "Duke ruajtur..."
                  : <><Save className="user-password-tab__save-icon" /> Ndrysho Fjalekalimin</>}
              </button>
            </div>
          </div>
        </div>

        {/* RIGHT — security requirements */}
        <div className="user-password-tab__info-col">
          <div className="user-password-tab__requirements-card">
            <div className="user-password-tab__req-header">
              <ShieldCheck className="user-password-tab__req-header-icon" />
              <p className="user-password-tab__req-title">Kerkesa per fjalekalim</p>
            </div>
            <p className="user-password-tab__req-desc">
              Fjalekalimi i ri duhet te plotesoje te gjitha kriteret e meposhme:
            </p>
            <ul className="user-password-tab__rules" aria-live="polite">
              {rules.map(({ key, label }) => {
                const pass = passwordRules[key];
                return (
                  <li key={key} className={`user-password-tab__rule ${pass ? "is-pass" : "is-pending"}`}>
                    <span className="user-password-tab__rule-badge">
                      {pass
                        ? <Check className="user-password-tab__rule-icon" />
                        : <X   className="user-password-tab__rule-icon" />}
                    </span>
                    {label}
                  </li>
                );
              })}
            </ul>
            <div className="user-password-tab__tip">
              <span className="user-password-tab__tip-label">Keshille:</span>
              Shmangni perdorimin e emrit, datëlindjes ose fjalëve te lehta.
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
